import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  useQueries,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import moment from 'moment-timezone';
import {
  normalizeTimeZoneForAtsCalendarApi,
  type CalendarEvent,
  type CalendarEventsFetchResult,
  type CalendarUserInfo,
} from './calendarEventsApi';
import { fetchCalendarEventsForUserBatched } from './batchedCalendarFetch';
import {
  buildWeekChunks,
  chunkAtWeekOffset,
  rangeIsoForWeekStartKey,
} from './calendarChunkUtils';
import {
  stashRemovedInterviewerEvents,
  takeSoftCachedInterviewerEvents,
  clearSoftInterviewerCacheEntry,
  REMOVED_INTERVIEWER_SOFT_CACHE_TTL_MS,
} from './removedInterviewerCalendarCache';

const CHUNK_GC_TIME_MS = 5 * 60 * 1000;

/**
 * Stable React Query key for one interviewer × one ISO-week bucket.
 * `from` / `to` are NOT part of the key — they are derived from `weekStartKey` + `timezone`
 * so the FullCalendar `datesSet` handshake does not duplicate fetches for the same week.
 */
export function buildUserWeekCalendarQueryKey(args: {
  userId: number;
  weekStartKey: string;
  timezone: string;
  atsJobId: number | null;
}): readonly unknown[] {
  return [
    'calendar-events',
    'user-week',
    args.userId,
    args.weekStartKey,
    args.timezone,
    args.atsJobId,
  ] as const;
}

function sortIds(ids: number[]): number[] {
  return Array.from(new Set(ids))
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);
}

function dedupeKey(evt: CalendarEvent, userId: number): string {
  return `${evt.id ?? ''}|${evt.start}|${evt.end}|${userId}`;
}

function isUserWeekChunkQueryKey(key: unknown): key is readonly unknown[] {
  return (
    Array.isArray(key) &&
    key.length === 6 &&
    key[0] === 'calendar-events' &&
    key[1] === 'user-week' &&
    typeof key[2] === 'number' &&
    typeof key[3] === 'string' &&
    typeof key[4] === 'string' &&
    (key[5] === null || typeof key[5] === 'number')
  );
}

export interface CalendarChunksQueryDescriptor {
  userId: number;
  weekStartKey: string;
  chunkIndex: number;
}

export interface UseInterviewerCalendarChunksQueryArgs {
  userIds: number[];
  /** Any instant in the visible week — normalized to ISO week start in `timezone` */
  anchorWeekStartIso: string;
  timezone: string;
  /** Number of 7-day chunks to load from the anchor (inclusive of visible week) */
  prefetchWeeks: number;
  /** Additional weeks beyond `prefetchWeeks` to background-prefetch */
  lookaheadPrefetchWeeks?: number;
  atsJobId?: number;
  enabled?: boolean;
  /** Legacy: skip busy fetch when timezone is not resolved to a real IANA zone */
  isLocalTimezone?: boolean;
  removedUserTtlMs?: number;
}

export interface UseInterviewerCalendarChunksQueryResult {
  mergedEvents: CalendarEvent[];
  /** True while any required visible-week (chunk 0) user query has no data and no soft cache */
  isBlockingBusyFetch: boolean;
  /** Any chunk query is currently fetching */
  isFetching: boolean;
  /** First error from chunk queries (for toast) */
  fetchError: Error | null;
  usersInfoById: Record<string, CalendarUserInfo>;
  queryDescriptors: CalendarChunksQueryDescriptor[];
}

export function useInterviewerCalendarChunksQuery({
  userIds,
  anchorWeekStartIso,
  timezone,
  prefetchWeeks,
  lookaheadPrefetchWeeks = 1,
  atsJobId,
  enabled = true,
  isLocalTimezone = false,
  removedUserTtlMs = REMOVED_INTERVIEWER_SOFT_CACHE_TTL_MS,
}: UseInterviewerCalendarChunksQueryArgs): UseInterviewerCalendarChunksQueryResult {
  const queryClient = useQueryClient();
  const sortedUserIds = useMemo(() => sortIds(userIds), [userIds]);

  /** Same canonical name ATS uses (`Asia/Calcutta` → `Asia/Kolkata`) so RQ keys match prefetch + API. */
  const zoneForKeysAndRanges = useMemo(
    () => normalizeTimeZoneForAtsCalendarApi(timezone),
    [timezone]
  );

  const anchorAlignedIso = useMemo(
    () =>
      moment(anchorWeekStartIso)
        .tz(zoneForKeysAndRanges)
        .startOf('week')
        .startOf('day')
        .toISOString(),
    [anchorWeekStartIso, zoneForKeysAndRanges]
  );

  const effectivePrefetch = Math.min(Math.max(prefetchWeeks, 1), 6);
  const effectiveLookahead = Math.min(Math.max(lookaheadPrefetchWeeks, 0), 4);

  const chunks = useMemo(
    () =>
      buildWeekChunks(
        anchorAlignedIso,
        zoneForKeysAndRanges,
        effectivePrefetch
      ),
    [anchorAlignedIso, zoneForKeysAndRanges, effectivePrefetch]
  );

  const queryDescriptors = useMemo((): Array<
    CalendarChunksQueryDescriptor & {
      rangeStartIso: string;
      rangeEndIso: string;
    }
  > => {
    const out: Array<
      CalendarChunksQueryDescriptor & {
        rangeStartIso: string;
        rangeEndIso: string;
      }
    > = [];
    for (let ci = 0; ci < chunks.length; ci += 1) {
      const c = chunks[ci];
      for (const userId of sortedUserIds) {
        out.push({
          userId,
          weekStartKey: c.weekStartKey,
          chunkIndex: ci,
          rangeStartIso: c.rangeStartIso,
          rangeEndIso: c.rangeEndIso,
        });
      }
    }
    return out;
  }, [chunks, sortedUserIds]);

  const jobKey = typeof atsJobId === 'number' && atsJobId > 0 ? atsJobId : null;

  const canRun =
    enabled &&
    !isLocalTimezone &&
    sortedUserIds.length > 0 &&
    chunks.length > 0;

  const queriesConfig = useMemo(
    () =>
      queryDescriptors.map((d) => ({
        queryKey: buildUserWeekCalendarQueryKey({
          userId: d.userId,
          weekStartKey: d.weekStartKey,
          timezone,
          atsJobId: jobKey,
        }),
        queryFn: async (ctx: QueryFunctionContext<readonly unknown[]>) => {
          const { queryKey, signal, client } = ctx;
          const rqState = client.getQueryState(queryKey);
          const rqData =
            client.getQueryData<CalendarEventsFetchResult>(queryKey);
          if (
            rqData !== null &&
            rqState &&
            rqState.status === 'success' &&
            !rqState.isInvalidated &&
            rqState.fetchStatus === 'idle'
          ) {
            return rqData;
          }
          const soft = takeSoftCachedInterviewerEvents(
            d.userId,
            d.weekStartKey
          );
          if (soft !== null) {
            return {
              events: soft,
              usersInfo: [{ id: d.userId }] as CalendarUserInfo[],
            };
          }
          const { rangeStartIso, rangeEndIso } = rangeIsoForWeekStartKey(
            d.weekStartKey,
            zoneForKeysAndRanges
          );
          const res = await fetchCalendarEventsForUserBatched({
            userId: d.userId,
            weekStartKey: d.weekStartKey,
            startDate: rangeStartIso,
            endDate: rangeEndIso,
            timezone: zoneForKeysAndRanges,
            atsJobId: jobKey,
            signal,
          });
          return res;
        },
        enabled: canRun,
        staleTime: 0,
        gcTime: CHUNK_GC_TIME_MS,
        // Visible week (chunk 0) re-confirms on every remount so a calendar
        // reconnect/disconnect made elsewhere is reflected when the wizard
        // re-opens. Lookahead stays cached for snappy week navigation.
        refetchOnMount: d.chunkIndex === 0 ? 'always' : false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // No retries on poll timeout. Each cycle already polls 15× over
        // 30s; if the backend stays PENDING that long, retrying with a
        // fresh `fetch_uuid` 3× more (TQ default) just multiplies the
        // load without changing the outcome — the recruiter can manually
        // refresh if needed. sch-28: caught this thundering-herd loop in
        // the network tab when 12 parallel queries (3 users × 4 weeks)
        // all timed out.
        retry: false,
        placeholderData: (prev: CalendarEventsFetchResult | undefined) => prev,
      })),
    [canRun, jobKey, queryDescriptors, timezone, zoneForKeysAndRanges]
  );

  const results = useQueries({
    queries: queriesConfig,
  });

  const prevSortedRef = useRef<number[]>([]);

  useLayoutEffect(() => {
    const prev = prevSortedRef.current;
    const next = sortedUserIds;
    if (prev.length === 0) {
      prevSortedRef.current = next;
      return;
    }
    const removed = prev.filter((id) => !next.includes(id));
    if (removed.length > 0) {
      for (const uid of removed) {
        const matches = queryClient.getQueryCache().findAll({
          predicate: (q) => {
            const key = q.queryKey;
            return isUserWeekChunkQueryKey(key) && (key[2] as number) === uid;
          },
        });
        for (const q of matches) {
          const key = q.queryKey;
          if (!isUserWeekChunkQueryKey(key)) {
            continue;
          }
          const weekStartKey = key[3] as string;
          const { data, status } = q.state;
          if (
            status === 'success' &&
            data !== undefined &&
            typeof data === 'object' &&
            'events' in data
          ) {
            stashRemovedInterviewerEvents(
              uid,
              weekStartKey,
              (data as CalendarEventsFetchResult).events ?? [],
              removedUserTtlMs
            );
          }
        }
        queryClient.removeQueries({
          predicate: (q) => {
            const key = q.queryKey;
            return isUserWeekChunkQueryKey(key) && (key[2] as number) === uid;
          },
        });
      }
    }
    prevSortedRef.current = next;
  }, [queryClient, removedUserTtlMs, sortedUserIds]);

  useEffect(() => {
    if (queryDescriptors.length !== results.length) {
      return;
    }
    results.forEach((r, i) => {
      if (!r.isSuccess || !r.data) {
        return;
      }
      const d = queryDescriptors[i];
      if (!d) {
        return;
      }
      clearSoftInterviewerCacheEntry(d.userId, d.weekStartKey);
    });
  }, [queryDescriptors, results]);

  useEffect(() => {
    if (!canRun) {
      return;
    }
    const startOffset = effectivePrefetch;
    const endOffset = effectivePrefetch + effectiveLookahead;
    for (let w = startOffset; w < endOffset; w += 1) {
      const c = chunkAtWeekOffset(anchorAlignedIso, zoneForKeysAndRanges, w);
      for (const uid of sortedUserIds) {
        const qk = buildUserWeekCalendarQueryKey({
          userId: uid,
          weekStartKey: c.weekStartKey,
          timezone: zoneForKeysAndRanges,
          atsJobId: jobKey,
        });
        const softPrefetch = takeSoftCachedInterviewerEvents(
          uid,
          c.weekStartKey
        );
        if (softPrefetch !== null) {
          queryClient.setQueryData(qk, {
            events: softPrefetch,
            usersInfo: [{ id: uid }] as CalendarUserInfo[],
          });
          continue;
        }
        const st = queryClient.getQueryState(qk);
        const existing =
          queryClient.getQueryData<CalendarEventsFetchResult>(qk);
        if (
          existing !== null &&
          st &&
          st.status === 'success' &&
          !st.isInvalidated &&
          st.fetchStatus === 'idle'
        ) {
          continue;
        }
        const { rangeStartIso, rangeEndIso } = rangeIsoForWeekStartKey(
          c.weekStartKey,
          zoneForKeysAndRanges
        );
        void queryClient.prefetchQuery({
          queryKey: qk,
          queryFn: ({ signal }) =>
            fetchCalendarEventsForUserBatched({
              userId: uid,
              weekStartKey: c.weekStartKey,
              startDate: rangeStartIso,
              endDate: rangeEndIso,
              timezone: zoneForKeysAndRanges,
              atsJobId: jobKey,
              signal,
            }),
          staleTime: 0,
          gcTime: CHUNK_GC_TIME_MS,
        });
      }
    }
  }, [
    anchorAlignedIso,
    canRun,
    effectiveLookahead,
    effectivePrefetch,
    jobKey,
    queryClient,
    sortedUserIds,
    zoneForKeysAndRanges,
  ]);

  const mergedEvents = useMemo(() => {
    const merged: CalendarEvent[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < queryDescriptors.length; i += 1) {
      const d = queryDescriptors[i];
      const r = results[i];
      let list: CalendarEvent[] = [];
      if (r?.isSuccess && r.data) {
        list = r.data.events ?? [];
      } else {
        const soft = takeSoftCachedInterviewerEvents(d.userId, d.weekStartKey);
        if (soft !== null) {
          list = soft;
        }
      }
      for (const evt of list) {
        const k = dedupeKey(evt, d.userId);
        if (seen.has(k)) {
          continue;
        }
        seen.add(k);
        const withUser =
          typeof evt.user_id === 'number' && Number.isFinite(evt.user_id)
            ? evt
            : { ...evt, user_id: d.userId };
        merged.push(withUser);
      }
    }
    return merged;
  }, [queryDescriptors, results]);

  const usersInfoById = useMemo(() => {
    const map: Record<string, CalendarUserInfo> = {};
    for (let i = 0; i < results.length; i += 1) {
      const r = results[i];
      if (!r?.isSuccess || !r.data) {
        continue;
      }
      for (const u of r.data.usersInfo) {
        const key = String(u.id);
        const prev = map[key];
        if (!prev) {
          map[key] = u;
          continue;
        }
        // `calendar_available` is a per-request-WINDOW flag — BE returns
        // it false only when THIS week-chunk's provider fetch errored
        // (`@events[email].instance_of?(String)`), NOT a stable "calendar
        // connected" property. A connected interviewer can be false in
        // one chunk and true in another. Without folding, the
        // last-iterated chunk wins, so a single bad window flips a
        // connected interviewer to "calendar not connected" even though
        // their events (merged across all chunks) are still visible —
        // the exact contradiction QA hit when adding interviewers from a
        // different week and navigating. Aggregate sticky-true: the
        // calendar is reachable if ANY successful window came back
        // not-false; only all-false stays disconnected.
        const available =
          prev.calendar_available !== false || u.calendar_available !== false;
        map[key] = { ...prev, ...u, calendar_available: available };
      }
    }
    return map;
  }, [results]);

  const fetchError = useMemo((): Error | null => {
    for (const r of results) {
      if (r.isError && r.error) {
        return r.error instanceof Error ? r.error : new Error(String(r.error));
      }
    }
    return null;
  }, [results]);

  const firstChunk = chunks[0];

  const isBlockingBusyFetch = useMemo(() => {
    if (!canRun || !firstChunk) {
      return false;
    }
    for (const uid of sortedUserIds) {
      const idx = queryDescriptors.findIndex(
        (d) => d.userId === uid && d.weekStartKey === firstChunk.weekStartKey
      );
      if (idx < 0) {
        return true;
      }
      const r = results[idx];
      const soft = takeSoftCachedInterviewerEvents(
        uid,
        firstChunk.weekStartKey
      );
      if (soft !== null) {
        continue;
      }
      if (!r) {
        return true;
      }
      if (r.isSuccess || r.isError) {
        continue;
      }
      return true;
    }
    return false;
  }, [canRun, firstChunk, queryDescriptors, results, sortedUserIds]);

  const isFetching = useMemo(
    () => results.some((r) => r.isFetching),
    [results]
  );

  return {
    mergedEvents,
    isBlockingBusyFetch,
    isFetching,
    fetchError,
    usersInfoById,
    queryDescriptors,
  };
}
